import React, { useState } from 'react';
import { RefreshCw, Shield } from 'lucide-react';

interface AdminButtonProps {
  token: string | null;
  onScrapeComplete?: () => void;
}

export const AdminButton: React.FC<AdminButtonProps> = ({ token, onScrapeComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showMenu, setShowMenu] = useState(false);

  const handleScrape = async () => {
    if (!token) {
      setMessage({ type: 'error', text: 'Vous devez être connecté' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/scrape-catalogue`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Catalogue mis à jour !' });
        
        // Call refresh callback
        if (onScrapeComplete) {
          onScrapeComplete();
        }
        
        // Recharger la page après 2 secondes
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setMessage({ type: 'error', text: `❌ ${data.error || 'Échec de la mise à jour'}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Erreur de connexion' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Message de confirmation */}
      {message && (
        <div className={`mb-2 p-3 rounded-lg shadow-lg text-sm font-semibold animate-in slide-in-from-bottom-2 ${
          message.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* Menu admin */}
      {showMenu && (
        <div className="mb-2 bg-white rounded-xl shadow-2xl p-4 border border-slate-200 animate-in slide-in-from-bottom-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Administration
          </div>
          <button
            onClick={handleScrape}
            disabled={isLoading}
            className="w-full flex items-center gap-3 bg-netto text-slate-900 px-4 py-3 rounded-lg font-semibold hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            <span>{isLoading ? 'Scraping...' : 'Mettre à jour le catalogue'}</span>
          </button>
          <p className="text-xs text-slate-500 mt-2">
            Récupère les dernières promotions Netto
          </p>
        </div>
      )}

      {/* Bouton principal */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 bg-slate-900 text-white px-4 py-3 rounded-full font-semibold hover:bg-slate-800 transition-all shadow-2xl active:scale-95"
        aria-label="Menu admin"
      >
        <Shield size={20} className="text-netto" />
        <span className="text-sm">Admin</span>
      </button>
    </div>
  );
};
